import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Close, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  IconButton,
  Divider,
  Grid,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";

import {
  getProjectServicesList,
  // toggleServiceEnabled,
  updateCurrentProjectService,
} from "../../../../actions/servicesActions";
import { resetServicesState } from "../../../../slices/servicesSlice";

const styles = {
  title: {
    my: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  content: {
    paddingY: 0,
    width: {
      md: "50vw",
    },
  },
  section: {
    mb: 3,
  },
};

const ProjectDetailsServices = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const projectServices = useSelector(
    (state) => state.services.projectServices
  );
  const project = useSelector((state) => state.projects.current);

  const [formData, setFormData] = useState({
    enabled: false,
    periodType: "quarterly",
    periodDayOfMonth: 1,
    notifyAdminOnCompletion: false,
    notifyAssociateOnApproval: false,
    adminEmails: "",
  });

  const service = project?.service || projectServices?.[0];

  useEffect(() => {
    if (props.open && params.id) {
      dispatch(getProjectServicesList({ projectId: params.id }));
    }
    return () => {
      if (!props.open) {
        dispatch(resetServicesState());
      }
    };
  }, [props.open, params.id]);

  useEffect(() => {
    if (service) {
      setFormData({
        enabled: service.enabled || false,
        periodType: service.rules?.period?.type || "quarterly",
        periodDayOfMonth: service.rules?.period?.dayOfMonth || 1,
        notifyAdminOnCompletion:
          service.notifications?.notifyAdminOnCompletion || false,
        notifyAssociateOnApproval:
          service.notifications?.notifyAssociateOnApproval || false,
        adminEmails: service.notifications?.adminEmails?.join(", ") || "",
      });
    }
  }, [service]);

  const handleClose = () => {
    props.onClose();
  };

  const handleSwitchChange = (name) => (e) => {
    setFormData({
      ...formData,
      [name]: e.target.checked,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = () => {
    if (!service?._id) return;

    const servicePayload = {
      enabled: formData.enabled,
      rules: {
        period: {
          type: formData.periodType,
          dayOfMonth: Number(formData.periodDayOfMonth),
        },
      },
      notifications: {
        notifyAdminOnCompletion: formData.notifyAdminOnCompletion,
        notifyAssociateOnApproval: formData.notifyAssociateOnApproval,
        adminEmails: formData.adminEmails
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email),
      },
    };

    dispatch(
      updateCurrentProjectService({
        projectId: params.id,
        serviceId: service._id,
        service: servicePayload,
        callback: () => {
          handleClose();
        },
      })
    );
  };

  const handleGoToFullService = () => {
    handleClose();
    navigate(`/dashboard/proyectos/${params.id}/servicio`);
  };

  return (
    <Dialog open={props.open} onClose={handleClose}>
      <DialogTitle>
        <Box sx={styles.title}>
          <Typography variant="h5">Servicios</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
      </DialogTitle>
      <DialogContent sx={styles.content}>
        {!service ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              No hay servicio configurado para este proyecto.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleGoToFullService}
            >
              Configurar Servicio
            </Button>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            <Box sx={styles.section}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Estado
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={handleSwitchChange("enabled")}
                  />
                }
                label="Servicio habilitado"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={styles.section}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Período
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de período</InputLabel>
                    <Select
                      name="periodType"
                      value={formData.periodType}
                      onChange={handleChange}
                      label="Tipo de período"
                    >
                      <MenuItem value="daily" disabled>
                        Diario
                      </MenuItem>
                      <MenuItem value="monthly" disabled>
                        Mensual
                      </MenuItem>
                      <MenuItem value="quarterly">Trimestral</MenuItem>
                      <MenuItem value="annual" disabled>
                        Anual
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Día del Mes</InputLabel>
                    <Select
                      name="periodDayOfMonth"
                      value={formData.periodDayOfMonth}
                      onChange={handleChange}
                      label="Día del Mes"
                      MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                    >
                      {[...Array(31)].map((_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {i + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={styles.section}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Notificaciones
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifyAdminOnCompletion}
                        onChange={handleSwitchChange("notifyAdminOnCompletion")}
                      />
                    }
                    label="Notificar admin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled
                        checked={formData.notifyAssociateOnApproval}
                        onChange={handleSwitchChange(
                          "notifyAssociateOnApproval"
                        )}
                      />
                    }
                    label="Notificar asociado"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Emails de Administradores"
                    name="adminEmails"
                    value={formData.adminEmails}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    placeholder="admin1@example.com, admin2@example.com"
                    helperText="Separa los emails con comas"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Button
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={handleGoToFullService}
              >
                Configuración completa
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Guardar
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDetailsServices;
